import React, { forwardRef, useEffect, useRef, ReactNode, FC, HTMLAttributes } from 'react';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';

export interface SectionProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
}

export interface PrintableContainerProps {
    allowPrint?: boolean;
    header?: ReactNode;
    body?: ReactNode;
    footer?: ReactNode;
    headerProps?: SectionProps;
    bodyProps?: SectionProps;
    footerProps?: SectionProps;
}

const useStyles = createUseStyles({
    '@global': {
        '@page': {
            size: 'A4',
            margin: '10mm',
        },
    },
    hiddenContainer: {
        position: 'absolute',
        zIndex: -1000,
        top: -1000,
        left: -1000,
        visibility: 'hidden',
        display: 'none',
        '@media print': {
            display: 'block',
            visibility: 'visible',
            position: 'static',
        },
    },
    visibleContainer: {
        position: 'absolute',
        zIndex: -1000,
        top: -1000,
        left: -1000,
        visibility: 'visible',
        '@media print': {
            display: 'block',
            visibility: 'visible',
            position: 'static',
        },
    },
    header: {
        backgroundColor: colors.blueOne,
        height: 80,
        textAlign: 'center',
        lineHeight: '80px',
        fontSize: '1.2rem',
        color: '#fff',
    },
    body: {
        padding: '5mm',
    },
    footer: {
        padding: '5mm',
    },
});

export const Header: FC<SectionProps> = ({ children, className = '', ...props }) => {
    const classes = useStyles();
    return (
        <div className={`${classes.header} ${className}`} {...props}>
            {children}
        </div>
    );
};

export const Body: FC<SectionProps> = ({ children, className = '', ...props }) => {
    const classes = useStyles();
    return (
        <div className={`${classes.body} ${className}`} {...props}>
            {children}
        </div>
    );
};

export const Footer: FC<SectionProps> = ({ children, className = '', ...props }) => {
    const classes = useStyles();
    return (
        <div className={`${classes.footer} ${className}`} {...props}>
            {children}
        </div>
    );
};

export const PrintableContainer = forwardRef<HTMLDivElement, PrintableContainerProps>(
    ({ header, body, footer, allowPrint = false, headerProps, bodyProps, footerProps }, ref) => {
        const shadowRootHostEl = useRef<HTMLDivElement | null>(null);
        const classes = useStyles();

        useEffect(() => {
            if (shadowRootHostEl.current && !shadowRootHostEl.current.shadowRoot) {
                const shadowRoot = shadowRootHostEl.current.attachShadow({ mode: 'open' });
                const wrapper = document.createElement('div');
                wrapper.innerHTML = `<p>Shadow DOM Content Here</p>`;
                shadowRoot.appendChild(wrapper);
            }
        }, []);

        return (
            <div ref={ref} className={allowPrint ? classes.visibleContainer : classes.hiddenContainer} role="group">
                {header && <Header {...headerProps}>{header}</Header>}
                {body && <Body {...bodyProps}>{body}</Body>}
                {footer && <Footer {...footerProps}>{footer}</Footer>}
                <div ref={shadowRootHostEl} role="none" />
            </div>
        );
    }
);
